import React, { useContext, useReducer, useMemo, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme as useTheme$1 } from '@material-ui/core/styles';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PropTypes from 'prop-types';
import produce from 'immer';
import { omitBy, pick, isUndefined } from 'lodash';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

var useStyles$1 = makeStyles({
  typographicCard: {
    color: '#ffffff',
    background: function background(_ref) {
      var color = _ref.color;
      return color;
    }
  }
});
var COLORS = ['#7491FF', '#21338C', '#0B79D0'];
var getColorByIndex = function getColorByIndex(index) {
  var colorIndex = index % 3;
  return COLORS[colorIndex];
};

var TypographicCard = function TypographicCard(_ref2) {
  var body = _ref2.body,
      _ref2$index = _ref2.index,
      index = _ref2$index === void 0 ? 0 : _ref2$index,
      _ref2$minHeight = _ref2.minHeight,
      minHeight = _ref2$minHeight === void 0 ? 230 : _ref2$minHeight;
  var color = getColorByIndex(index);
  var classes = useStyles$1({
    color: color
  });
  var theme = useTheme();
  var isMd = useMediaQuery(theme.breakpoints.up('md'));
  var isSm = useMediaQuery(theme.breakpoints.up('sm'));
  return /*#__PURE__*/React.createElement(Paper, {
    className: classes.typographicCard
  }, /*#__PURE__*/React.createElement(Box, {
    p: isSm ? 4 : 2,
    minHeight: minHeight
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: isMd ? 'h4' : 'h5',
    component: 'p'
  }, body)));
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded = ["children", "showPartials"];
var Context = /*#__PURE__*/React.createContext(null);
var useCarouselControls = function useCarouselControls() {
  var _useContext = useContext(Context),
      next = _useContext.next,
      prev = _useContext.prev,
      swiping = _useContext.swiping;

  return {
    next: next,
    prev: prev,
    swiping: swiping
  };
};
var init = {
  total: 0,
  startIndex: 0,
  displayCount: 0,
  swiping: false,
  hasPartials: false
};

var reducer = function reducer(state, action) {
  switch (action.type) {
    case 'init':
      return produce(state, function (draft) {
        draft.total = action.total;
        draft.displayCount = action.count;
        draft.startIndex = action.reset ? 0 : draft.startIndex;
        draft.hasPartials = action.hasPartials;
      });

    case 'prev':
      return produce(state, function (draft) {
        draft.startIndex = Math.max(draft.startIndex - draft.displayCount, 0);
      });

    case 'next':
      return produce(state, function (draft) {
        draft.startIndex = Math.min(draft.startIndex + draft.displayCount, draft.total - 1);
      });

    case 'swipe':
      return produce(state, function (draft) {
        draft.swiping = action.enabled;
      });
  }
};

var Carousel = function Carousel(_ref) {
  var children = _ref.children,
      reset = _ref.reset;

  var _useReducer = useReducer(reducer, init),
      _useReducer$ = _useReducer[0],
      total = _useReducer$.total,
      startIndex = _useReducer$.startIndex,
      swiping = _useReducer$.swiping,
      displayCount = _useReducer$.displayCount,
      hasPartials = _useReducer$.hasPartials,
      dispatch = _useReducer[1];

  var value = useMemo(function () {
    var goNext = function goNext() {
      return dispatch({
        type: 'next'
      });
    };

    var goPrev = function goPrev() {
      return dispatch({
        type: 'prev'
      });
    }; // hasPartials purposefully shows a partial last item when total > 1, so we need to account for the last item


    var displayNext = hasPartials ? startIndex + displayCount <= total : startIndex + displayCount < total;
    var next = displayNext ? goNext : undefined;
    var prev = startIndex > 0 ? goPrev : undefined;
    return {
      init: function init(total, count, hasPartials) {
        return dispatch({
          type: 'init',
          total: total,
          count: count,
          reset: reset,
          hasPartials: hasPartials
        });
      },
      enableSwipe: function enableSwipe(enabled) {
        return dispatch({
          type: 'swipe',
          enabled: enabled
        });
      },
      next: next,
      prev: prev,
      startIndex: startIndex,
      total: total,
      swiping: swiping
    };
  }, [total, startIndex, displayCount, swiping, reset]);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: value
  }, children);
};
Carousel.propTypes = {
  children: PropTypes.any,
  reset: PropTypes["boolean"]
};
var useIconStyles = makeStyles(function (theme) {
  return {
    iconRoot: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      padding: theme.spacing(2),
      margin: theme.spacing(0.5)
    }
  };
});
var SeeAll = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Link, _extends({}, props, {
    innerRef: ref
  }));
});
SeeAll.displayName = 'SeeAll';
var CarouselControls = function CarouselControls(_ref2) {
  var seeAll = _ref2.seeAll;

  var _useIconStyles = useIconStyles(),
      iconRoot = _useIconStyles.iconRoot;

  var _useCarouselControls = useCarouselControls(),
      next = _useCarouselControls.next,
      prev = _useCarouselControls.prev,
      swiping = _useCarouselControls.swiping;

  var showNavButtons = next != null || prev != null;

  if (!swiping) {
    return /*#__PURE__*/React.createElement(Box, null, seeAll && /*#__PURE__*/React.createElement(Button, {
      component: SeeAll,
      color: "primary",
      to: seeAll
    }, "See All"), showNavButtons && /*#__PURE__*/React.createElement(IconButton, {
      disabled: !prev,
      "aria-label": "previous",
      className: iconRoot,
      color: "primary",
      onClick: prev
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      size: "xs",
      icon: faChevronLeft
    })), showNavButtons && /*#__PURE__*/React.createElement(IconButton, {
      disabled: !next,
      "aria-label": "next",
      className: iconRoot,
      color: "primary",
      onClick: next
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      size: "xs",
      icon: faChevronRight
    })));
  }

  return !seeAll ? null : /*#__PURE__*/React.createElement(Box, {
    m: 1
  }, /*#__PURE__*/React.createElement(Button, {
    component: SeeAll,
    color: "primary",
    "aria-label": "see all",
    to: seeAll
  }, "See All"));
};
CarouselControls.propTypes = {
  seeAll: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    path: PropTypes.string.isRequired
  })])
};
var useStyles = makeStyles(function (theme) {
  return {
    swipeGridItem: {
      flexShrink: 0
    },
    swipeContainer: {
      width: '100%',
      overflowX: 'scroll',
      maxWidth: "calc(100vw - " + theme.spacing(1) + "px)"
    },
    containingPartials: {
      overflow: 'hidden',
      width: '148%'
    }
  };
});
var CarouselBody = function CarouselBody(_ref3) {
  var children = _ref3.children,
      _ref3$showPartials = _ref3.showPartials,
      showPartials = _ref3$showPartials === void 0 ? {} : _ref3$showPartials,
      rest = _objectWithoutPropertiesLoose(_ref3, _excluded);

  var spacing = rest.spacing;

  var _useContext2 = useContext(Context),
      init = _useContext2.init,
      enableSwipe = _useContext2.enableSwipe,
      startIndex = _useContext2.startIndex,
      total = _useContext2.total,
      swiping = _useContext2.swiping;

  var theme = useTheme$1();
  var classes = useStyles(rest);
  var isXs = useMediaQuery(theme.breakpoints.only('xs'));
  var isSm = useMediaQuery(theme.breakpoints.up('sm'));
  var isMd = useMediaQuery(theme.breakpoints.up('md'));
  var isLg = useMediaQuery(theme.breakpoints.up('lg'));
  var itemColSpan = isLg && rest.lg || isMd && rest.md || isSm && rest.sm || isXs && rest.xs;
  var completelyVisiblePageSize = Math.floor(12 / itemColSpan);
  var shouldShowPartials = isLg && showPartials.lg || isMd && showPartials.md || isSm && showPartials.sm || isXs && showPartials.xs;
  var pageSizeWithPartials = shouldShowPartials && itemColSpan < 11 ? completelyVisiblePageSize + 1 : completelyVisiblePageSize;
  var hasPartials = pageSizeWithPartials !== completelyVisiblePageSize;
  var nonNullChildren = React.Children.toArray(children).filter(function (child) {
    return child != null;
  });
  var childrenCount = React.Children.count(nonNullChildren);
  useEffect(function () {
    init(childrenCount, completelyVisiblePageSize, hasPartials);
  }, [init, childrenCount, completelyVisiblePageSize]);
  useEffect(function () {
    enableSwipe(isXs);
  }, [isXs, enableSwipe]);
  var sizes = omitBy(pick(rest, ['xs', 'sm', 'md', 'lg']), isUndefined);
  var gridItemClassName;
  var containerProps = {};
  var managedChildren = React.Children.toArray(children);

  if (!swiping) {
    var managedChildrenSize = total > 0 ? startIndex + pageSizeWithPartials : 0;
    managedChildren = managedChildren.slice(startIndex, managedChildrenSize);

    if (hasPartials && managedChildren.length > 1) {
      containerProps.className = classes.containingPartials;
    }
  } else {
    gridItemClassName = classes.swipeGridItem;
    containerProps = {
      p: 1.5,
      m: -1.5,
      className: classes.swipeContainer
    };
  }

  return /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, containerProps, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    wrap: "nowrap",
    spacing: spacing
  }, managedChildren.map(function (i, idx) {
    return /*#__PURE__*/React.createElement(Grid, _extends({
      key: idx,
      item: true,
      className: gridItemClassName
    }, sizes), i);
  }))));
};
CarouselBody.propTypes = {
  showPartials: PropTypes.object,
  children: PropTypes.any,
  spacing: PropTypes.number,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number
};

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useCarouselControls: useCarouselControls,
    Carousel: Carousel,
    CarouselControls: CarouselControls,
    CarouselBody: CarouselBody
});

export { index as GridAlignedCarousel, TypographicCard };
//# sourceMappingURL=index.module.js.map
